<script lang="ts">
  type SyncAction = {
    label: string;
    description?: string;
    badgeClass: string;
    iconPaths: string[];
    handler: () => void;
  };

  export let lastSyncTime: string = "";
  export let globalHealth: string = "Healthy";
  export let loading: boolean = false;
  export let isVisible: boolean = true;
  export let controlsDisabled: boolean = false;
  export let secondaryActionVisible: boolean = false;
  export let secondaryActionLabel: string = "";
  export let secondaryActionPendingLabel: string = "";
  export let secondaryActionPending: boolean = false;
  export let syncProgress: { current: number; total: number; message: string } | null = null;
  export let title: string = "Dashboard";
  export let syncActions: SyncAction[] = [];
  export let labels: { lastSync?: string; syncing?: string; syncButton?: string; show?: string; hide?: string } = {};

  const defaultLabels = { lastSync: "Last Sync:", syncing: "Syncing...", syncButton: "Sync", show: "Show dashboard", hide: "Hide dashboard" };
  $: activeLabels = { ...defaultLabels, ...labels };

  export let onToggleVisibility: () => void = () => {};
  export let onSecondaryAction: () => void = () => {};

  let syncDropdownOpen = false;
  let userMenuOpen = false;

  function toggleSyncDropdown() {
    if (loading || controlsDisabled) return;
    syncDropdownOpen = !syncDropdownOpen;
    userMenuOpen = false;
  }

  function toggleUserMenu() {
    userMenuOpen = !userMenuOpen;
    syncDropdownOpen = false;
  }

  function handleSyncOption(handler: () => void) {
    syncDropdownOpen = false;
    handler();
  }

  function handleVisibilityToggle() {
    userMenuOpen = false;
    onToggleVisibility();
  }

  const formatLastSync = (value: string) => {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return `${date.toISOString().slice(0, 10)} ${date.toISOString().slice(11, 19)} UTC`;
  };

  $: healthDotClass =
    globalHealth === "Healthy"
      ? "bg-green-500"
      : globalHealth === "Degraded"
        ? "bg-amber-500"
        : "bg-red-500";
</script>

<svelte:window
  on:click={() => {
    syncDropdownOpen = false;
    userMenuOpen = false;
  }}
/>

<div class="flex flex-col gap-4 px-0 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-1">
  <div class="flex min-w-0 items-center justify-center gap-4 sm:justify-start">
    <h1 class="m-0 whitespace-nowrap text-center text-xl font-bold tracking-[0.3px] text-white sm:text-left">{title}</h1>
  </div>
  <div class="grid min-w-0 grid-cols-[auto_auto_auto] items-center justify-center gap-2 sm:ml-auto sm:flex sm:flex-wrap sm:justify-end sm:gap-3">
    <div class="col-span-3 flex min-w-0 flex-wrap items-center justify-center gap-2 text-sm sm:w-auto sm:justify-start">
      <span class="text-white/50">{activeLabels.lastSync}</span>
      <span class="min-w-0 break-all font-['Courier_New',monospace] font-semibold text-white/90">{formatLastSync(lastSyncTime)}</span>
    </div>

    <div
      class={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold ${
        globalHealth === "Healthy"
          ? "border-green-500/40 bg-green-500/10"
          : globalHealth === "Degraded"
            ? "border-amber-500/40 bg-amber-500/10"
            : "border-red-500/40 bg-red-500/10"
      }`}
    >
      <span class="h-2.5 w-2.5 rounded-full {healthDotClass} shadow-[0_0_8px_currentColor]"></span>
      <span class="text-white">{globalHealth}</span>
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="relative" on:click|stopPropagation>
      <button
        on:click={toggleSyncDropdown}
        class="flex min-w-[122px] cursor-pointer items-center justify-center gap-2 rounded-lg border border-white/15 bg-[#34344a] px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-[1px] hover:bg-[#3f4059] disabled:cursor-not-allowed disabled:opacity-60 sm:px-5"
        disabled={loading || controlsDisabled || !isVisible || syncActions.length === 0}
      >
        {#if loading}
          <svg class="h-4 w-4 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          {activeLabels.syncing}
        {:else}
          <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {activeLabels.syncButton}
          <svg class="h-3.5 w-3.5 shrink-0 transition-transform duration-200 {syncDropdownOpen ? 'rotate-180' : ''}" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        {/if}
      </button>

      {#if syncDropdownOpen && !loading && syncActions.length > 0}
        <div class="absolute right-0 top-[calc(100%+6px)] z-50 min-w-[210px] overflow-hidden rounded-lg border border-white/10 bg-[#24283a] shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
          {#each syncActions as action, i}
            <button
              on:click={() => handleSyncOption(action.handler)}
              class="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-white transition-colors hover:bg-white/10 {i < syncActions.length - 1 ? 'border-b border-white/5' : ''}"
            >
              <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md {action.badgeClass}">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  {#each action.iconPaths as d}
                    <path stroke-linecap="round" stroke-linejoin="round" {d} />
                  {/each}
                </svg>
              </span>
              <div>
                <div class="font-semibold text-white">{action.label}</div>
                {#if action.description}
                  <div class="mt-0.5 text-xs font-normal text-white/50">{action.description}</div>
                {/if}
              </div>
            </button>
          {/each}
        </div>
      {/if}
    </div>

    {#if secondaryActionVisible}
      <button
        on:click={onSecondaryAction}
        class="cursor-pointer rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={loading || controlsDisabled || secondaryActionPending}
      >
        {secondaryActionPending ? secondaryActionPendingLabel : secondaryActionLabel}
      </button>
    {/if}

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="relative" on:click|stopPropagation>
      <button
        on:click={toggleUserMenu}
        class="flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.06] px-3 py-2 text-white/80 transition-all hover:bg-white/10 hover:text-white"
        aria-haspopup="true"
        aria-expanded={userMenuOpen}
        aria-label="User menu"
      >
        <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M4 20.5c1.5-3 4.5-4.5 8-4.5s6.5 1.5 8 4.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <svg class="h-3.5 w-3.5 shrink-0 transition-transform duration-200 {userMenuOpen ? 'rotate-180' : ''}" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {#if userMenuOpen}
        <div class="absolute right-0 top-[calc(100%+6px)] z-50 min-w-[220px] overflow-hidden rounded-lg border border-white/10 bg-[#24283a] shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
          <button
            on:click={handleVisibilityToggle}
            class="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-white transition-colors hover:bg-white/10"
          >
            <svg class="h-4 w-4 shrink-0 text-white/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
              {#if isVisible}
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M14.12 14.12a3 3 0 1 1-4.24-4.24M1 1l22 22" stroke-linecap="round" stroke-linejoin="round" />
              {:else}
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke-linecap="round" stroke-linejoin="round" />
              {/if}
            </svg>
            {isVisible ? activeLabels.hide : activeLabels.show}
          </button>
        </div>
      {/if}
    </div>
  </div>
</div>

{#if syncProgress && loading}
  <div class="animate-[slideDown_0.3s_ease-out] border-b border-white/10 bg-[#1b1e2e] px-6 py-4">
    <div class="mb-2 flex items-center justify-between text-sm">
      <span class="font-semibold text-white">{syncProgress.message}</span>
      <span class="font-['Courier_New',monospace] text-white/70">{syncProgress.current} / {syncProgress.total}</span>
    </div>
    <div class="h-2 w-full overflow-hidden rounded bg-white/10">
      <div
        class="h-full rounded bg-[linear-gradient(90deg,#22c55e,#16a34a)] shadow-[0_0_10px_rgba(34,197,94,0.5)] transition-[width] duration-300 ease-out"
        style="width: {(syncProgress.current / syncProgress.total) * 100}%"
      />
    </div>
  </div>
{/if}
